import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';
import ProfileEditModal from './ProfileEditModal';
import { useNotification } from '../../contexts/NotificationContext';

const AdminProfilesTab = ({ profiles, loading, deleteProfile, toggleStatus, reloadProfiles, pagination, onPageChange, searchTerm = '', filterBy = 'all', onSearchChange, onFilterChange }) => {
  const { toast } = useNotification();
  const [editingSlug, setEditingSlug] = useState(null); // profile id being edited
  const [newSlug, setNewSlug] = useState('');
  const [updatingSlug, setUpdatingSlug] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null); // profile being edited in modal
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // Local, immediately-editable copy of the search box so typing never stalls
  // waiting for the network request triggered by each keystroke.
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const debounceRef = useRef(null);

  // Keep in sync if the parent resets/changes the term externally
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchInputChange = (value) => {
    setLocalSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange && onSearchChange(value);
    }, 400);
  };

  // Function to download Excel
  const downloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/profiles/export/excel`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'perfiles_contact.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel descargado exitosamente');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Error al descargar el Excel: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDownloadingExcel(false);
    }
  };

  // Slug editing functions
  const startEditingSlug = (profile) => {
    setEditingSlug(profile.id);
    setNewSlug(profile.slug);
  };

  const cancelEditingSlug = () => {
    setEditingSlug(null);
    setNewSlug('');
    setUpdatingSlug(false);
  };

  const updateSlug = async (profileId) => {
    if (!newSlug.trim()) {
      toast.error('El slug no puede estar vacío');
      return;
    }

    setUpdatingSlug(true);
    try {
      const response = await axios.put(
        `${API}/admin/profiles/${profileId}/slug`,
        null,
        {
          params: { new_slug: newSlug.trim() },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        toast.success(`URL actualizada: ${response.data.old_slug} → ${response.data.new_slug}`);
        reloadProfiles(); // Reload to show updated slug
        cancelEditingSlug();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al actualizar URL';
      toast.error(errorMessage);
      console.error('Error updating slug:', error);
    } finally {
      setUpdatingSlug(false);
    }
  };

  const filteredProfiles = profiles || [];
  const isInitialLoad = loading && filteredProfiles.length === 0;

  return (
    <div className="space-y-6">
      {/* Filters — always mounted so the search input never loses focus while typing */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Buscar por nombre, empresa, usuario o email..."
            value={localSearchTerm}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            className="form-input w-full"
          />
          {loading && !isInitialLoad && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 animate-pulse">
              Buscando...
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={filterBy}
            onChange={(e) => onFilterChange && onFilterChange(e.target.value)}
            className="form-input"
          >
            <option value="all">Todos los perfiles</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
          <button
            onClick={downloadExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
            title="Descargar todos los perfiles en Excel"
            disabled={downloadingExcel || loading}
          >
            {downloadingExcel ? (
              <>
                <span className="animate-spin">⏳</span>
                Descargando...
              </>
            ) : (
              <>
                📊 Excel
              </>
            )}
          </button>
          <button
            onClick={reloadProfiles}
            className="btn-secondary px-3 py-2 text-sm"
            title="Recargar perfiles"
            disabled={loading}
          >
            {loading ? '...' : '↻'}
          </button>
        </div>
      </div>

      {isInitialLoad ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-300 mx-auto"></div>
          <p className="mt-2 text-gray-600">Cargando perfiles...</p>
        </div>
      ) : filteredProfiles.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="users" className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {localSearchTerm || filterBy !== 'all'
              ? "No hay perfiles que coincidan con los filtros"
              : "No hay perfiles en la plataforma"}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Total de perfiles: {pagination?.total || profiles?.length || 0}
          </p>
        </div>
      ) : (
        <>
          {/* .apt-table forces real table layout on mobile — the global
              `table{display:block!important}` rule (App.css) breaks this
              table's columns; a class selector beats that rule at equal
              !important specificity, and the wrapper's own overflow-x
              already gives mobile a normal horizontal-scroll table. */}
          <style>{`
            .apt-table{display:table !important}
            .apt-sticky{position:sticky;right:0;background:#fff;box-shadow:-6px 0 8px -6px rgba(0,0,0,.08)}
            thead .apt-sticky{background:#F8FAFC;box-shadow:none}
            tbody tr:hover .apt-sticky{background:#F9FAFB}
          `}</style>
          <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}>
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="apt-table w-full text-sm">
                <thead style={{ background: '#F8FAFC' }}>
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Perfil</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Usuario</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Empresa</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Redes</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Visitas</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Estado</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Creado</th>
                    <th className="text-right px-4 py-3 apt-sticky"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProfiles.map((profile) => (
                    <tr key={profile.id} className="border-t border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900 whitespace-nowrap">{profile.name} {profile.lastname}</div>
                        {editingSlug === profile.id ? (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs text-gray-400">/pages/</span>
                            <input
                              type="text"
                              value={newSlug}
                              onChange={(e) => setNewSlug(e.target.value)}
                              className="text-xs border border-gray-300 rounded px-1.5 py-0.5 w-24 focus:outline-none focus:ring-2 focus:ring-sky-500"
                              disabled={updatingSlug}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') updateSlug(profile.id);
                                if (e.key === 'Escape') cancelEditingSlug();
                              }}
                            />
                            <button
                              onClick={() => updateSlug(profile.id)}
                              disabled={updatingSlug}
                              className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded hover:bg-green-700 disabled:opacity-50"
                              title="Guardar URL"
                            >
                              {updatingSlug ? '...' : '✓'}
                            </button>
                            <button
                              onClick={cancelEditingSlug}
                              disabled={updatingSlug}
                              className="text-xs bg-gray-500 text-white px-1.5 py-0.5 rounded hover:bg-gray-600"
                              title="Cancelar"
                            >
                              ✗
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-xs text-gray-500">/pages/{profile.slug}</span>
                            <button
                              onClick={() => startEditingSlug(profile)}
                              className="text-xs text-sky-600 hover:text-sky-800"
                              title="Editar URL"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">{profile.user_name}</div>
                        <div className="text-xs text-gray-500">{profile.user_email}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {profile.company && <div className="font-medium text-gray-900">{profile.company}</div>}
                        {profile.position && <div className="text-xs text-gray-500">{profile.position}</div>}
                        {!profile.company && !profile.position && <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap gap-1">
                          {profile.instagram && <span className="px-1.5 py-0.5 bg-pink-100 text-pink-800 text-[10px] rounded">IG</span>}
                          {profile.linkedin && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] rounded">LI</span>}
                          {profile.facebook && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] rounded">FB</span>}
                          {profile.website && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-800 text-[10px] rounded">Web</span>}
                          {!profile.instagram && !profile.linkedin && !profile.facebook && !profile.website && <span className="text-gray-300">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-900">{profile.visits || 0}</td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          profile.is_active !== false ? 'text-green-700' : 'text-red-700'
                        }`} style={{ background: profile.is_active !== false ? '#ECFDF5' : '#FEF2F2' }}>
                          <span className={`w-1.5 h-1.5 rounded-full ${profile.is_active !== false ? 'bg-green-500' : 'bg-red-500'}`} />
                          {profile.is_active !== false ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-500 whitespace-nowrap">{new Date(profile.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 align-top text-right apt-sticky">
                        <div className="flex justify-end gap-1.5 flex-wrap">
                          <a
                            href={`/pages/${profile.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1 text-xs font-medium rounded-lg transition whitespace-nowrap"
                            style={{ background: '#F1F5F9', color: '#475569' }}
                          >
                            Ver
                          </a>
                          <button
                            onClick={() => setEditingProfile(profile)}
                            className="px-2 py-1 text-xs font-medium rounded-lg transition whitespace-nowrap"
                            style={{ background: 'rgba(2, 132, 199, 0.08)', color: '#0284C7' }}
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => toggleStatus(profile.id, profile.is_active !== false)}
                            className="px-2 py-1 text-xs font-medium rounded-lg transition whitespace-nowrap"
                            style={{
                              background: profile.is_active !== false ? '#FFFBEB' : '#ECFDF5',
                              color: profile.is_active !== false ? '#B45309' : '#059669',
                            }}
                          >
                            {profile.is_active !== false ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            onClick={() => deleteProfile(profile.id)}
                            className="px-2 py-1 text-xs font-medium rounded-lg transition whitespace-nowrap"
                            style={{ background: '#FEF2F2', color: '#B91C1C' }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Pagination Info & Controls */}
      {pagination && pagination.total_pages > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600" data-testid="profiles-pagination-info">
            Mostrando {profiles?.length || 0} de {pagination.total} perfiles (Página {pagination.page} de {pagination.total_pages})
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={!pagination.has_prev || loading}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="profiles-prev-page"
            >
              Anterior
            </button>
            {/* Page number buttons */}
            {Array.from({ length: Math.min(pagination.total_pages, 5) }, (_, i) => {
              let pageNum;
              if (pagination.total_pages <= 5) {
                pageNum = i + 1;
              } else if (pagination.page <= 3) {
                pageNum = i + 1;
              } else if (pagination.page >= pagination.total_pages - 2) {
                pageNum = pagination.total_pages - 4 + i;
              } else {
                pageNum = pagination.page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  disabled={loading}
                  className={`w-10 h-10 text-sm font-medium rounded-lg transition-colors ${
                    pageNum === pagination.page
                      ? 'bg-sky-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  } disabled:opacity-40`}
                  data-testid={`profiles-page-${pageNum}`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={!pagination.has_next || loading}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="profiles-next-page"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {editingProfile && (
        <ProfileEditModal
          profile={editingProfile}
          onClose={() => setEditingProfile(null)}
          onSave={() => {
            reloadProfiles();
            setEditingProfile(null);
          }}
        />
      )}
    </div>
  );
};

// Admin Quotations Tab Component
export default AdminProfilesTab;
